import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import { useObtenerPorID } from '../hooks/EventosHook';
import { crearEventoService, uploadImagenEvento } from '../../../service/eventos/Eventos';
import { obtenerRecintoSelect, crearRecintoService, uploadMapaEvento } from '../../../service/recintos/Recintos';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
  id: number;
};

const today = new Date().toISOString().split('T')[0];
const getNoteSchema = (isEditing: boolean) => Yup.object().shape({
  nombre: Yup.string()
    .trim()
    .test(
      'not-only-spaces',
      'El nombre del evento no puede contener solo espacios.',
      (value) => value?.trim() !== ''
    )
    .required('Nombre del evento es requerido.'),
  fecha_inicio: isEditing
    ? Yup.date().nullable().required('La fecha inicio es requerida')
    : Yup.date()
        .min(today, 'Debe ser igual o mayor a la fecha actual')
        .nullable()
        .required('La fecha inicio es requerida'),
  fecha_fin: Yup.date()
    .min(Yup.ref('fecha_inicio'), 'No puede ser menor a la fecha inicio.')
    .required('La fecha fin es requerida'),
});

const ModalCRU: React.FC<Props> = ({ show, handleClose, id }) => {
  const [loading, setLoading] = useState(false);
  const [recintoOpts, setRecintoOpts] = useState<Array<{ value: number; label: string }>>([]);
  const [showNuevoRecinto, setShowNuevoRecinto] = useState(false);
  const [nuevoRecinto, setNuevoRecinto] = useState({ nombre: '', direccion: '', aforo_max: '' });
  const [savingRecinto, setSavingRecinto] = useState(false);

  const { initValues, loadingRequest } = useObtenerPorID(id);

  useEffect(() => {
    obtenerRecintoSelect()
      .then(setRecintoOpts)
      .catch(() => {});
  }, []);

  const validationSchema = useMemo(() => getNoteSchema(id > 0), [id]);

  const formEvento = useFormik({
    initialValues: initValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await crearEventoService(values);
        if (values.id) {
          window.location.reload();
        } else {
          window.location.reload();
        }
      } catch (error: any) {
        if (error && error.response) {
          const { message } = error.response.data;
          SimpleAlert(message, 3000, 'error');
          setLoading(false);
        }
      }
    },
  });

  const handleCrearRecinto = async () => {
    if (!nuevoRecinto.nombre.trim()) { SimpleAlert('El nombre es requerido.', 3000, 'error'); return; }
    if (!nuevoRecinto.aforo_max || Number(nuevoRecinto.aforo_max) <= 0) { SimpleAlert('El aforo máximo es requerido.', 3000, 'error'); return; }
    setSavingRecinto(true);
    try {
      const resp = await crearRecintoService({ nombre: nuevoRecinto.nombre, direccion: nuevoRecinto.direccion, aforo_max: Number(nuevoRecinto.aforo_max) });
      const newId = resp?.data?.doc?.data?.id;
      const opts = await obtenerRecintoSelect();
      setRecintoOpts(opts);
      if (newId) formEvento.setFieldValue('recinto_id', newId);
      setShowNuevoRecinto(false);
      setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' });
      SimpleAlert('Recinto creado y seleccionado', 2500, 'success');
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message || 'Error al crear el recinto.', 3000, 'error');
    } finally {
      setSavingRecinto(false);
    }
  };

  const validateImageDimensions = (file: File, width: number, height: number): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img.naturalWidth <= width && img.naturalHeight <= height);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };
      img.src = url;
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        SimpleAlert('El archivo seleccionado no es una imagen', 3000, 'error');
        event.target.value = '';
        return;
      }

      if (fieldName === 'imagen') {
        const isValidSize = await validateImageDimensions(file, 1920, 1080);
        if (!isValidSize) {
          SimpleAlert('La imagen del evento no debe superar 1920x1080 píxeles', 3000, 'error');
          event.target.value = '';
          return;
        }
      }

      setLoading(true);
      try {
        const response = fieldName === 'mapa_ubicaciones'
          ? await uploadMapaEvento(file)
          : await uploadImagenEvento(file);
        const rutaImagen = response?.doc?.data?.imageUrl || response?.data?.imageUrl;

        if (rutaImagen) {
          formEvento.setFieldValue(fieldName, rutaImagen);
          SimpleAlert('Imagen subida correctamente', 2000, 'success');
        } else {
          throw new Error('No se recibió la URL de la imagen del servidor');
        }
      } catch (error: any) {
        SimpleAlert('Error al subir la imagen. Intenta de nuevo.', 3000, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-800px"
      show={show}
      onHide={() => {
        formEvento.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">
            {id > 0 ? 'Editar evento' : 'Nuevo evento'}
          </h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formEvento.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>
        <div className="modal-body mx-5 padding-top-00">
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formEvento.handleSubmit}
          >
            <div className="row wh-0100 mb-3">
              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100  padd-left input-name-form"
                  data-kt-element="input"
                  placeholder="Ingrese el nombre del evento"
                  maxLength={40}
                  {...formEvento.getFieldProps('nombre')}
                ></input>
                {formEvento.touched.nombre && formEvento.errors.nombre && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formEvento.errors.nombre}</span>
                  </div>
                )}
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Fecha inicio
                  </label>
                </div>
                <input
                  className="form-control"
                  data-kt-element="input"
                  type="date"
                  {...formEvento.getFieldProps('fecha_inicio')}
                  onChange={(event) => {
                    formEvento.setFieldValue(
                      'fecha_inicio',
                      event.target.value
                    );
                  }}
                />
                {formEvento.touched.fecha_inicio &&
                  formEvento.errors.fecha_inicio && (
                    <div className="fv-plugins-message-container text-danger w-100">
                      <span role="alert">{formEvento.errors.fecha_inicio}</span>
                    </div>
                  )}
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6  mt-5">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Fecha fin
                  </label>
                </div>
                <input
                  className="form-control"
                  data-kt-element="input"
                  type="date"
                  {...formEvento.getFieldProps('fecha_fin')}
                  onChange={(event) => {
                    formEvento.setFieldValue('fecha_fin', event.target.value);
                  }}
                />
                {formEvento.touched.fecha_fin && formEvento.errors.fecha_fin && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formEvento.errors.fecha_fin}</span>
                  </div>
                )}
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    URL de la Imagen (Opcional)
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100 padd-left"
                  data-kt-element="input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'imagen')}
                ></input>
                <div className="text-muted fs-7 mt-1">Dimensiones máximas: 1920x1080 px</div>
                {formEvento.values.imagen && (
                  <div className="text-success mt-1 fs-7">
                    ✓ Imagen cargada ({formEvento.values.imagen.split('/').pop()})
                  </div>
                )}
              </div>

              {/* Selector de Recinto */}
              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12 mt-5">
                <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold fs-6">Recinto</label>
                  <button type="button" className="btn btn-link btn-sm p-0 text-primary" onClick={() => setShowNuevoRecinto(true)}>
                    + Nuevo recinto
                  </button>
                </div>
                <select
                  className="form-select"
                  value={formEvento.values.recinto_id ?? ''}
                  onChange={(e) =>
                    formEvento.setFieldValue(
                      'recinto_id',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                >
                  <option value="">— Sin recinto —</option>
                  {recintoOpts.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mapa de Ubicaciones */}
              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    Mapa de Ubicaciones (jpg/png, máx 5 MB)
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100 padd-left"
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={(e) => handleImageUpload(e, 'mapa_ubicaciones')}
                />
                {formEvento.values.mapa_ubicaciones && (
                  <div className="text-success mt-1 fs-7">
                    ✓ Mapa cargado ({(formEvento.values.mapa_ubicaciones as string).split('/').pop()})
                  </div>
                )}
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    Ubicación (Opcional)
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100 padd-left"
                  data-kt-element="input"
                  placeholder="Ej: Patio Principal - Santuario de Fátima"
                  {...formEvento.getFieldProps('ubicacion')}
                ></input>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    Descripción (Opcional)
                  </label>
                </div>
                <textarea
                  className="form-control mb-2 w-100 padd-left"
                  data-kt-element="input"
                  rows={3}
                  placeholder="Ingrese una descripción sobre el evento"
                  {...formEvento.getFieldProps('descripcion')}
                ></textarea>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    Nombre del Organizador (Opcional)
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100 padd-left"
                  data-kt-element="input"
                  placeholder="Ej: Organizador"
                  {...formEvento.getFieldProps('organizador_nombre')}
                ></input>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-auto">
                    Imagen del Organizador (Opcional)
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100 padd-left"
                  data-kt-element="input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'organizador_imagen')}
                ></input>
                {formEvento.values.organizador_imagen && (
                  <div className="text-success mt-1 fs-7">
                    ✓ Imagen cargada ({formEvento.values.organizador_imagen.split('/').pop()})
                  </div>
                )}
              </div>

            </div>

            {/* Checkbox Activar Menús */}
            <div className="d-flex align-items-center mt-5">
              <div className="form-check form-check-custom form-check-solid">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mostrar_menus"
                  checked={formEvento.values.mostrar_menus || false}
                  onChange={(e) =>
                    formEvento.setFieldValue('mostrar_menus', e.target.checked)
                  }
                />
                <label className="form-check-label fw-bold ms-2" htmlFor="mostrar_menus">
                  Activar menús
                </label>
              </div>
            </div>

            {/* Checkbox Activar Recargas */}
            <div className="d-flex align-items-center mt-3">
              <div className="form-check form-check-custom form-check-solid">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mostrar_recargas"
                  checked={formEvento.values.mostrar_recargas || false}
                  onChange={(e) =>
                    formEvento.setFieldValue('mostrar_recargas', e.target.checked)
                  }
                />
                <label className="form-check-label fw-bold ms-2" htmlFor="mostrar_recargas">
                  Activar Recargas
                </label>
              </div>
            </div>

            {/* Checkbox Visible en App */}
            <div className="d-flex align-items-center mt-3">
              <div className="form-check form-check-custom form-check-solid">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="visible_app"
                  checked={formEvento.values.visible_app ?? true}
                  onChange={(e) =>
                    formEvento.setFieldValue('visible_app', e.target.checked)
                  }
                />
                <label className="form-check-label fw-bold ms-2" htmlFor="visible_app">
                  Visible en App
                </label>
              </div>
            </div>


            <div className="d-flex justify-content-end mt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                data-kt-users-modal-action="cancel"
                onClick={() => {
                  formEvento.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-kt-users-modal-action="submit"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>

    {/* ── Mini-modal: Nuevo recinto ── */}
    <Modal
      backdrop="static"
      dialogClassName="modal-dialog mw-500px"
      show={showNuevoRecinto}
      onHide={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">Nuevo recinto</h2>
          <button type="button" className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>✕</button>
        </div>
        <div className="modal-body mx-5">
          <div className="mb-4">
            <label className="required fw-bold fs-6 mb-2">Nombre</label>
            <input className="form-control" placeholder="Ej: Cintermex" maxLength={100} value={nuevoRecinto.nombre} onChange={(e) => setNuevoRecinto((p) => ({ ...p, nombre: e.target.value }))} />
          </div>
          <div className="mb-4">
            <label className="fw-bold fs-6 mb-2">Dirección</label>
            <input className="form-control" placeholder="Ej: Av. Fundidora 501, Monterrey" maxLength={255} value={nuevoRecinto.direccion} onChange={(e) => setNuevoRecinto((p) => ({ ...p, direccion: e.target.value }))} />
          </div>
          <div className="mb-4">
            <label className="required fw-bold fs-6 mb-2">Aforo máximo (personas)</label>
            <input className="form-control" type="number" min={1} placeholder="Ej: 8000" value={nuevoRecinto.aforo_max} onChange={(e) => setNuevoRecinto((p) => ({ ...p, aforo_max: e.target.value }))} />
          </div>
          <div className="d-flex justify-content-end mt-6">
            <button type="button" className="btn btn-light me-3" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={handleCrearRecinto} disabled={savingRecinto}>
              {savingRecinto ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</> : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
};
export { ModalCRU };
